// src/components/workspace/ChatTab.js
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { MessageSquare } from "lucide-react";

function ChatTab({
  messages,
  newMessage,
  setNewMessage,
  handleSendMessage,
  currentUser,
  problem,
  containerClassName = "",
  messagesClassName = "",
  // Inside the workspace comms rail the surrounding panel already provides the
  // frame and the "Chat" label, so the card chrome and header are dropped.
  embedded = false,
}) {
  const chatEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const previousMessageCountRef = useRef(0);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [newMessageIds, setNewMessageIds] = useState(new Set());
  const [attachment, setAttachment] = useState(null);

  // Auto-scroll to bottom only when new messages arrive (not on initial load)
  useEffect(() => {
    if (!hasInitialized && messages.length > 0) {
      // First load - scroll the chat container only, not the entire page
      setTimeout(() => {
        const container = messagesContainerRef.current;
        if (container) {
          // Scroll the container to bottom, not the entire page
          container.scrollTop = container.scrollHeight;
        }
      }, 100);
      setHasInitialized(true);
      previousMessageCountRef.current = messages.length;
      return;
    }

    // Only auto-scroll if there's a new message (count increased)
    if (messages.length > previousMessageCountRef.current) {
      // Mark new messages for highlighting
      const newIds = new Set();
      messages.slice(previousMessageCountRef.current).forEach((msg) => {
        if (msg.senderId !== currentUser.uid) {
          newIds.add(msg.id);
        }
      });
      setNewMessageIds(newIds);

      // Remove highlight after 3 seconds
      setTimeout(() => {
        setNewMessageIds(new Set());
      }, 3000);

      // Check if user is near the bottom before auto-scrolling
      const container = messagesContainerRef.current;
      if (container) {
        const isNearBottom = 
          container.scrollHeight - container.scrollTop - container.clientHeight < 150;
        
        // Only auto-scroll if user is already near the bottom
        if (isNearBottom) {
          // Scroll container, not entire page
          container.scrollTop = container.scrollHeight;
        }
      }
      previousMessageCountRef.current = messages.length;
    }
  }, [messages, hasInitialized, currentUser]);

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (userId) => {
    // Consistent solid color per participant — distinct enough to tell
    // speakers apart at a glance without introducing decorative gradients.
    const colors = [
      "bg-blue-600",
      "bg-slate-600",
      "bg-emerald-600",
      "bg-amber-600",
      "bg-sky-600",
      "bg-rose-600",
    ];
    const index = userId.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate();
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const counterpartId =
    currentUser.uid === problem?.helperId ? problem?.clientId : problem?.helperId;

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setAttachment(file);
    }
  };

  const renderMessageContent = (text) => {
    if (!text) return null;
    const parts = [];
    const regex = /```([\s\S]*?)```/g;
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push({
          type: "text",
          content: text.slice(lastIndex, match.index),
        });
      }
      parts.push({ type: "code", content: match[1] });
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < text.length) {
      parts.push({ type: "text", content: text.slice(lastIndex) });
    }

    return parts.map((part, idx) => {
      if (part.type === "code") {
        return (
          <pre
            key={idx}
            className="mt-1 rounded-lg bg-slate-900 text-slate-100 text-xs sm:text-sm p-3 overflow-auto font-mono whitespace-pre-wrap"
          >
            {part.content}
          </pre>
        );
      }
      return (
        <p key={idx} className="text-sm break-words whitespace-pre-wrap">
          {part.content}
        </p>
      );
    });
  };

  return (
    <div
      className={`flex flex-col overflow-hidden bg-white ${
        embedded ? "" : "rounded-lg border border-slate-200 shadow-sm"
      } ${containerClassName}`}
    >
      {!embedded && (
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-slate-500" />
            Chat
            {messages.length > 0 && (
              <span className="ml-2 text-xs text-slate-500 font-normal">
                {messages.length}{" "}
                {messages.length === 1 ? "message" : "messages"}
              </span>
            )}
          </h2>
        </div>
      )}

      {/* Messages Area */}
      <div
        ref={messagesContainerRef}
        className={`flex-1 overflow-y-auto overscroll-contain px-6 py-4 space-y-4 bg-slate-50 min-h-[400px] ${messagesClassName}`}
      >
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <MessageSquare className="w-10 h-10 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500 text-sm">
                No messages yet. Start the conversation!
              </p>
            </div>
          </div>
        ) : (
          messages.map((msg) => {
            const isCurrentUser = msg.senderId === currentUser.uid;
            const isSystem = msg.type === "system";
            const isReadByOther =
              isCurrentUser && counterpartId
                ? Array.isArray(msg.readBy) && msg.readBy.includes(counterpartId)
                : false;
            const hasTimestamp = Boolean(msg.createdAt?.seconds || msg.createdAt?.toDate);

            if (isSystem) {
              return (
                <div key={msg.id} className="flex justify-center">
                  <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-xs font-medium">
                    {msg.text || msg.message}
                  </div>
                </div>
              );
            }

            const isNew = newMessageIds.has(msg.id);

            return (
              <motion.div
                key={msg.id}
                initial={isNew ? { scale: 0.95, opacity: 0 } : false}
                animate={isNew ? { scale: 1, opacity: 1 } : {}}
                transition={{ duration: 0.2 }}
                className={`flex ${isCurrentUser ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`flex ${
                    isCurrentUser ? "flex-row-reverse" : "flex-row"
                  } items-end space-x-2 max-w-xl`}
                >
                  {/* Avatar */}
                  <div
                    className={`w-8 h-8 rounded-full ${getAvatarColor(
                      msg.senderId
                    )} flex items-center justify-center text-white font-bold text-xs flex-shrink-0 ${
                      isCurrentUser ? "ml-2" : "mr-2"
                    }`}
                  >
                    {getInitials(msg.senderName)}
                  </div>

                  {/* Message Bubble */}
                  <div className={isCurrentUser ? "items-end" : "items-start"}>
                    <div
                      className={`px-4 py-2 rounded-2xl transition-all ${
                        isCurrentUser
                          ? "bg-blue-600 text-white rounded-br-sm"
                          : "bg-white text-slate-900 border border-slate-200 rounded-bl-sm shadow-sm"
                      } ${
                        isNew ? "ring-2 ring-blue-400 ring-opacity-75 shadow-lg" : ""
                      }`}
                    >
                      {!isCurrentUser && (
                        <p className="text-xs font-semibold text-slate-600 mb-1">
                          {msg.senderName}
                        </p>
                      )}
                      {renderMessageContent(msg.message)}
                      {msg.attachmentUrl && (
                        <div className="mt-2 space-y-2">
                          <div className="text-xs text-slate-500">
                            Attachment:{" "}
                            <a
                              href={msg.attachmentUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-blue-600 underline"
                            >
                              {msg.attachmentName || "file"}
                            </a>
                          </div>
                          {msg.attachmentType?.startsWith("image/") && (
                            <img
                              src={msg.attachmentUrl}
                              alt={msg.attachmentName || "attachment"}
                              className="max-h-48 rounded-md border border-slate-200"
                            />
                          )}
                          {msg.attachmentPreview &&
                            (msg.attachmentType?.startsWith("text/") ||
                              msg.attachmentType === "application/json" ||
                              msg.attachmentType?.includes("javascript")) && (
                              <pre className="rounded-lg bg-slate-900 text-slate-100 text-xs sm:text-sm p-3 overflow-auto font-mono whitespace-pre-wrap">
                                {msg.attachmentPreview}
                              </pre>
                            )}
                        </div>
                      )}
                    </div>
                    <div
                      className={`flex items-center mt-1 px-1 ${
                        isCurrentUser ? "justify-end" : "justify-start"
                      }`}
                    >
                      <span className="text-xs text-slate-400">
                        {formatTime(msg.createdAt)}
                      </span>
                      {isCurrentUser && (
                        <span
                          className={`text-xs ml-2 ${
                            isReadByOther ? "text-blue-500" : "text-slate-400"
                          }`}
                          title={
                            !hasTimestamp
                              ? "Sending..."
                              : isReadByOther
                              ? "Read"
                              : "Sent"
                          }
                        >
                          {isReadByOther ? "✓✓" : hasTimestamp ? "✓" : "…" }
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Typing Indicator */}
      {/* {isTyping && (
        <div className="px-6 py-2 text-xs text-slate-500 italic">
          Someone is typing...
        </div>
      )} */}

      {/* Message Input */}
      <div className="px-6 py-4 border-t border-slate-200 bg-white">
        <form
          onSubmit={(e) =>
            handleSendMessage(e, {
              attachment,
              onClearAttachment: () => setAttachment(null),
            })
          }
          className="flex flex-col gap-3"
        >
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message... (use ``` for code blocks)"
              className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <label className="cursor-pointer text-sm font-medium text-blue-600 hover:text-blue-700 whitespace-nowrap">
              Attach
              <input
                type="file"
                className="hidden"
                onChange={handleFileChange}
              />
            </label>
            <button
              type="submit"
              disabled={!newMessage.trim() && !attachment}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Send
            </button>
          </div>
          {attachment && (
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <span className="px-2 py-1 bg-slate-100 rounded border border-slate-200">
                {attachment.name} ({Math.round(attachment.size / 1024)} KB)
              </span>
              <button
                type="button"
                onClick={() => setAttachment(null)}
                className="text-rose-500 hover:underline"
              >
                Remove
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default ChatTab;
